import React, { useMemo } from 'react'
import { HStack, Box, Text } from '@chakra-ui/react'
import { useGetTrademarksQuery } from '@/generated/graphql'
import { VALID_TRADEMARK_STATUSES } from '@/utils/constants'
import PaginatedDropdown from '@/components/doppel_design/paginated_dropdown/paginated_dropdown'
import {
  DOPPEL_GREY_800,
  DOPPEL_SECONDARY_GREEN,
  DOPPEL_TEXT_BLACK,
  DOPPEL_WHITE,
} from '@/utils/style'

type PaginatedTrademarkDropdownProps = {
  spoofingEntityId: string
  selectedTrademarkId?: string
  selectedTrademarkMark?: string
  onChange: (trademarkId: string, trademarkMark: string) => void
  emptyOptionLabel?: string
  includedEmptyOption?: boolean
  pageSize?: number
  width?: string
  placeholder?: string
  isDisabled?: boolean
}

/**
 * A paginated dropdown for trademarks with search functionality.
 * Uses the PaginatedDropdown component for consistent UI and behavior.
 */
const PaginatedTrademarkDropdown: React.FC<PaginatedTrademarkDropdownProps> = ({
  spoofingEntityId,
  selectedTrademarkId,
  selectedTrademarkMark = '',
  onChange,
  emptyOptionLabel = 'No Trademark',
  includedEmptyOption = true,
  pageSize = 10,
  width = '260px',
  placeholder = 'Select Trademark',
  isDisabled = false,
}) => {
  // Create a selected trademark object if we have an ID and mark
  const selectedTrademark = useMemo(() => {
    if (!selectedTrademarkId) return undefined
    return {
      id: selectedTrademarkId,
      mark: selectedTrademarkMark || '',
      status: 'active', // Default status if not known
      // Adding required fields with default values to satisfy TypeScript
      registration_number: '',
      holder_name: '',
      holder_address: '',
      holder_country_code: '',
      name: '',
    }
  }, [selectedTrademarkId, selectedTrademarkMark])

  // Adapter function to fetch trademarks with pagination and search
  const useFetchTrademarks = ({ searchQuery, limit, offset }) => {
    const { data, loading, refetch } = useGetTrademarksQuery({
      variables: {
        where: {
          spoofing_entity_id: { _eq: spoofingEntityId },
          status: { _in: VALID_TRADEMARK_STATUSES },
          ...(searchQuery ? { mark: { _ilike: `%${searchQuery}%` } } : {}),
        },
        limit: limit,
        offset,
      },
      skip: !spoofingEntityId,
    })

    // Filter out the currently selected trademark
    let filteredTrademarks = data?.trademarks || []
    if (selectedTrademarkId) {
      filteredTrademarks = filteredTrademarks.filter(
        (tm) => tm.id !== selectedTrademarkId,
      )
    }

    // If empty option is enabled and this is the first page, add it to the results
    const emptyOption = {
      id: '',
      mark: emptyOptionLabel || 'No Trademark',
      status: '',
      // Adding required fields with default values
      registration_number: '',
      holder_name: '',
      holder_address: '',
      holder_country_code: '',
      name: '',
    }

    return {
      items: [emptyOption, ...filteredTrademarks],
      loading,
      refetch,
    }
  }

  // Adapter function to fetch total count of trademarks
  const useFetchTrademarkCount = (searchQuery) => {
    const { data, loading, refetch } = useGetTrademarksQuery({
      variables: {
        where: {
          spoofing_entity_id: { _eq: spoofingEntityId },
          status: { _in: VALID_TRADEMARK_STATUSES },
          ...(searchQuery ? { mark: { _ilike: `%${searchQuery}%` } } : {}),
        },
      },
      skip: !spoofingEntityId,
    })

    return {
      count: data?.trademarks?.length || 0,
      loading,
      refetch,
    }
  }

  // Handle selection of trademark or empty option
  const handleSelect = (item: any) => {
    if (!item) {
      onChange('', emptyOptionLabel)
    } else {
      onChange(item.id, item.mark || '')
    }
  }

  // Render the trademark item with status indicator
  const renderTrademarkLabel = (trademark: any) => {
    // Special case for the 'No Trademark' option - no status indicator
    if (trademark.mark === emptyOptionLabel || trademark.id === '') {
      return (
        <Text color={DOPPEL_GREY_800} fontSize="sm" fontWeight="medium">
          {trademark.mark}
        </Text>
      )
    }

    // For all trademarks (including selected ones), show the status indicator
    return (
      <HStack spacing={3}>
        <Box
          alignItems="center"
          bg={trademark.status === 'active' ? DOPPEL_SECONDARY_GREEN : DOPPEL_GREY_800}
          border="1px solid"
          borderColor={
            trademark.status === 'active' ? DOPPEL_SECONDARY_GREEN : DOPPEL_GREY_800
          }
          borderRadius="md"
          display="flex"
          fontWeight="600"
          justifyContent="center"
          minWidth="70px"
          px={2}
          py={0.5}
        >
          <Text
            color={DOPPEL_WHITE}
            fontSize="xs"
            fontWeight="semibold"
            textTransform="uppercase"
          >
            {trademark.status || 'pending'}
          </Text>
        </Box>

        <Text
          color={
            trademark.id === selectedTrademarkId ? DOPPEL_WHITE : DOPPEL_TEXT_BLACK
          }
          fontSize="sm"
          fontWeight="medium"
        >
          {trademark.mark}
        </Text>
      </HStack>
    )
  }

  return (
    <Box width={width}>
      <PaginatedDropdown
        autoTriggerSearch={true}
        isDisabled={isDisabled}
        itemToKey={(item) => item.id}
        itemToLabel={renderTrademarkLabel}
        menuLabel={placeholder}
        onSelect={handleSelect}
        pageSize={pageSize}
        searchPlaceholderText="Search trademarks..."
        selectedItem={selectedTrademark}
        textColor={DOPPEL_TEXT_BLACK}
        useFetchCountQuery={useFetchTrademarkCount}
        useFetchDataQuery={useFetchTrademarks}
      />
    </Box>
  )
}

export default PaginatedTrademarkDropdown
